import React, { useState, useEffect } from 'react';
import { useThemeMode } from '../../ThemeContext';
import { dataAPI } from '../../helpers/api_service';
import { formatDateToAPI } from '../../helpers/UtilsFunctions';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { TextField, FormControl, Select, MenuItem } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { enGB } from 'date-fns/locale';
import AppIconBlue from '../../assets/AppIconBlue.svg';
import '../../styles/loading-animations.css';

const DocumentForm = ({ isOpen, onClose, onSubmit, editData = null, selectedFolder = null }) => {
  const { mode } = useThemeMode();
  const isEditMode = !!editData;
  
  // Custom theme for Material-UI components to match current styling
  const customTheme = createTheme({
    palette: {
      mode: mode,
      primary: {
        main: '#26C3C0',
      },
      background: {
        default: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
        paper: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
      },
      text: {
        primary: mode === 'dark' ? '#FFFFFF' : '#222222',
        secondary: mode === 'dark' ? '#666' : '#999',
      },
    },
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: '6px',
              fontFamily: "'Urbanist', Arial, sans-serif",
              fontSize: '14px',
              '& fieldset': {
                borderColor: mode === 'dark' ? '#444' : '#ddd',
                transition: 'border-color 0.2s ease',
              },
              '&:hover fieldset': {
                borderColor: '#26C3C0',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#26C3C0',
              },
            },
            '& .MuiInputLabel-root': {
              color: mode === 'dark' ? '#666' : '#999',
              fontFamily: "'Urbanist', Arial, sans-serif",
              fontSize: '14px',
              '&.Mui-focused': {
                color: '#26C3C0',
              },
            },
          },
        },
      },
      MuiDatePicker: {
        styleOverrides: {
          root: {
            '& .MuiPickersDay-root': {
              fontFamily: "'Urbanist', Arial, sans-serif",
              '&.Mui-selected': {
                backgroundColor: '#26C3C0',
                '&:hover': {
                  backgroundColor: '#1f9e9b',
                },
              },
            },
          },
        },
      },
    },
  });

  const [formData, setFormData] = useState({
    name: '',
    version: '',
    expiry_date: null,
    file: null
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showFormLoading, setShowFormLoading] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  // Initialize form data when editData changes
  useEffect(() => {
    if (editData) {
      setFormData({
        name: editData.name || '',
        version: editData.version || '',
        expiry_date: editData.expiry_date ? new Date(editData.expiry_date) : null,
        file: null // File is not pre-filled in edit mode
      });
    } else {
      setFormData({
        name: '',
        version: '',
        expiry_date: null,
        file: null
      });
    }
    setError('');
  }, [editData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        file: file
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation: file is required only for new documents
    const requiredFields = ['name', 'version', 'expiry_date'];
    if (!isEditMode) {
      requiredFields.push('file');
    }
    
    const missingFields = requiredFields.filter(field => !formData[field]);
    if (missingFields.length > 0) {
      setError('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setShowFormLoading(true);
    setShowValidation(true);
    
    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('version', formData.version);
      formDataToSend.append('expiry_date', formatDateToAPI(formData.expiry_date));
      
      // Include folder_id if a folder is selected
      // For new documents: use selectedFolder
      // For editing: use the folder where the document currently exists, or selectedFolder if changing folders
      if (isEditMode && editData.folder_id) {
        // When editing, include the current folder_id of the document
        formDataToSend.append('folder_id', editData.folder_id);
      } else if (selectedFolder && selectedFolder.id) {
        // For new documents or when changing folders, use the selected folder
        formDataToSend.append('folder_id', selectedFolder.id);
      }
      
      // Only append file if it's selected (for both new and edit)
      if (formData.file) {
        formDataToSend.append('file', formData.file);
      }

      console.log(`${isEditMode ? 'Editing' : 'Uploading'} document with data:`, {
        name: formData.name,
        version: formData.version,
        expiry_date: formData.expiry_date,
        fileName: formData.file?.name,
        folder_id: isEditMode && editData.folder_id ? editData.folder_id : (selectedFolder?.id || 'No folder selected'),
        isEditMode,
        editDataFolderId: editData?.folder_id,
        selectedFolderId: selectedFolder?.id
      });

      // Debug: Log FormData contents
      console.log('FormData contents:');
      for (let [key, value] of formDataToSend.entries()) {
        console.log(`${key}:`, value);
      }

      let response;
      if (isEditMode) {
        // Call edit API
        response = await dataAPI.editDocument(editData.id, formDataToSend, { showLoading: false });
        console.log('Edit successful:', response);
      } else {
        // Call upload API
        response = await dataAPI.uploadDocument(formDataToSend, { showLoading: false });
        console.log('Upload successful:', response);
      }
      
      // Success - call the parent onSubmit callback
      onSubmit(response);
      
      // Reset form
      setFormData({
        name: '',
        version: '',
        expiry_date: null,
        file: null
      });
      
      onClose();
    } catch (error) {
      console.error(`${isEditMode ? 'Edit' : 'Upload'} error:`, error);
      setError(error.message || `Failed to ${isEditMode ? 'edit' : 'upload'} document. Please try again.`);
    } finally {
      setIsSubmitting(false);
      setShowFormLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      version: '',
      expiry_date: '',
      file: null
    });
    setError('');
    setShowFormLoading(false);
    setShowValidation(false);
    onClose();
  };

  // Check if all required fields are filled
  const requiredFields = ['name', 'version', 'expiry_date'];
  if (!isEditMode) {
    requiredFields.push('file');
  }
  const isFormComplete = requiredFields.every(field => formData[field]);

  if (!isOpen) return null;

  return (
    <ThemeProvider theme={customTheme}>
      <>
        {/* Backdrop */}
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            backdropFilter: 'blur(2px)'
          }}
        />
        
        {/* Modal */}
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          width: '420px',
          height: 'calc(100vh - 40px)',
          backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
          boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.3)',
          zIndex: 1001,
          overflowY: 'auto',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s ease-in-out',
          borderRadius: '12px'
        }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 20px 8px 20px',
          borderBottom: `1px solid ${mode === 'dark' ? '#333' : '#e0e0e0'}`
        }}>
          <h2 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: mode === 'dark' ? '#FFFFFF' : '#222222',
            margin: 0,
            fontFamily: "'Urbanist', Arial, sans-serif"
          }}>
            {isEditMode ? 'Edit Document' : 'Upload Document'}
          </h2>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '6px',
              borderRadius: '4px',
              color: mode === 'dark' ? '#FFFFFF' : '#666666',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CloseIcon style={{ fontSize: '20px' }} />
          </button>
        </div>

        {/* Form Loading Overlay */}
        {showFormLoading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1003,
            backgroundColor: mode === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(4px)',
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '12px'
          }}>
            {/* Fill Animation Loading Indicator */}
            <div className="fill-animation-container">
              {/* Background container */}
              <div className="fill-animation-background" />
              
              {/* Progress fill animation */}
              <div className="fill-animation-progress" />
              
              {/* AppIconBlue with pulse animation */}
              <img 
                src={AppIconBlue} 
                alt="Loading" 
                className={`fill-animation-icon pulse-animation-icon`}
              />
            </div>
          </div>
        )}

        {/* Form Content */}
        <form onSubmit={handleSubmit} style={{ padding: '16px 20px' }}>
          {/* Error Message */}
          {error && (
            <div style={{
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#ffebee',
              border: '1px solid #f44336',
              borderRadius: '6px',
              color: '#d32f2f',
              fontSize: '14px',
              fontFamily: "'Urbanist', Arial, sans-serif"
            }}>
              {error}
            </div>
          )}

          {/* Document Name */}
          <div style={{ marginBottom: '16px' }}>
            <TextField
              size="small"
              required
              label="Document Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              disabled={isSubmitting}
              error={showValidation && !formData.name.trim()}
              helperText={showValidation && !formData.name.trim() ? 'Document name is required' : ''}
              style={{
                width: '100%',
                backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
                color: mode === 'dark' ? '#FFFFFF' : '#222222'
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s ease',
                  '& fieldset': {
                    borderColor: mode === 'dark' ? '#444' : '#ddd',
                  },
                  '&:hover fieldset': {
                    borderColor: '#26C3C0',
                    boxShadow: '0 4px 8px rgba(38,195,192,0.2)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#26C3C0',
                    boxShadow: '0 0 0 2px rgba(38,195,192,0.1)',
                  },
                  '&.Mui-error fieldset': {
                    borderColor: '#f44336',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: mode === 'dark' ? '#999' : '#666',
                  fontSize: '14px',
                },
                '& .MuiFormHelperText-root': {
                  color: '#f44336',
                  fontSize: '12px',
                },
              }}
            />
          </div>

          {/* Version */}
          <div style={{ marginBottom: '16px' }}>
            <TextField
              size="small"
              required
              label="Version"
              value={formData.version}
              onChange={(e) => handleInputChange('version', e.target.value)}
              disabled={isSubmitting}
              error={showValidation && !formData.version.trim()}
              helperText={showValidation && !formData.version.trim() ? 'Version is required' : ''}
              style={{
                width: '100%',
                backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
                color: mode === 'dark' ? '#FFFFFF' : '#222222'
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s ease',
                  '& fieldset': {
                    borderColor: mode === 'dark' ? '#444' : '#ddd',
                  },
                  '&:hover fieldset': {
                    borderColor: '#26C3C0',
                    boxShadow: '0 4px 8px rgba(38,195,192,0.2)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#26C3C0',
                    boxShadow: '0 0 0 2px rgba(38,195,192,0.1)',
                  },
                  '&.Mui-error fieldset': {
                    borderColor: '#f44336',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: mode === 'dark' ? '#999' : '#666',
                  fontSize: '14px',
                },
                '& .MuiFormHelperText-root': {
                  color: '#f44336',
                  fontSize: '12px',
                },
              }}
            />
          </div>

          {/* Expiry Date */}
          <div style={{ marginBottom: '16px' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                             <DatePicker
                 value={formData.expiry_date}
                 onChange={(newValue) => {
                   handleInputChange('expiry_date', newValue);
                 }}
                 format="dd-MM-yyyy"
                                   slotProps={{
                    textField: {
                      label: "Document Review Date",
                      fullWidth: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '6px',
                          backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
                          color: mode === 'dark' ? '#FFFFFF' : '#222222',
                          fontSize: '14px',
                          fontFamily: "'Urbanist', Arial, sans-serif",
                          padding: '10px 16px',
                          '& fieldset': {
                            borderColor: formData.expiry_date ? '#26C3C0' : (mode === 'dark' ? '#444' : '#ddd'),
                            transition: 'border-color 0.2s ease',
                          },
                          '&:hover fieldset': {
                            borderColor: '#26C3C0',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#26C3C0',
                          },
                        },
                        '& .MuiInputBase-input': {
                          padding: '0',
                          height: 'auto',
                          lineHeight: 'normal',
                          backgroundColor: 'transparent',
                        },
                        '& .MuiInputBase-root': {
                          backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
                        },
                        backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
                      }
                    }
                  }}
               />
            </LocalizationProvider>
          </div>

          {/* File Upload Area */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{
              border: `2px dashed ${mode === 'dark' ? '#444' : '#ddd'}`,
              borderRadius: '8px',
              padding: '20px 16px',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'border-color 0.2s ease',
              backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
              position: 'relative'
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = '#26C3C0';
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = mode === 'dark' ? '#444' : '#ddd';
            }}
            >
              <input
                type="file"
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  cursor: 'pointer'
                }}
              />
              <CloudUploadIcon style={{
                fontSize: '42px',
                color: '#26C3C0',
                marginBottom: '12px'
              }} />
              <div style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#26C3C0',
                marginBottom: '6px',
                fontFamily: "'Urbanist', Arial, sans-serif"
              }}>
                {isEditMode ? 'Click to Upload New File (Optional)' : 'Click to Upload Document'}
              </div>
              <div style={{
                fontSize: '12px',
                color: mode === 'dark' ? '#999' : '#666',
                fontFamily: "'Urbanist', Arial, sans-serif"
              }}>
                Supported file types: .pdf, .doc, .docx
                {isEditMode && <br />}
                {isEditMode && <span style={{ color: '#26C3C0' }}>Leave empty to keep current file</span>}
              </div>
              {formData.file && (
                <div style={{
                  marginTop: '12px',
                  fontSize: '14px',
                  color: '#26C3C0',
                  fontFamily: "'Urbanist', Arial, sans-serif"
                }}>
                  Selected: {formData.file.name}
                </div>
              )}
              {isEditMode && !formData.file && editData?.file_path && (
                <div style={{
                  marginTop: '12px',
                  fontSize: '14px',
                  color: mode === 'dark' ? '#999' : '#666',
                  fontFamily: "'Urbanist', Arial, sans-serif"
                }}>
                  Current file: {editData.name}
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting || !isFormComplete}
            style={{
              width: '100%',
              padding: '14px 24px',
              backgroundColor: isFormComplete && !isSubmitting ? '#26C3C0' : (mode === 'dark' ? '#444' : '#666'),
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: isFormComplete && !isSubmitting ? 'pointer' : 'not-allowed',
              fontFamily: "'Urbanist', Arial, sans-serif",
              transition: 'background-color 0.2s ease',
              boxShadow: isFormComplete && !isSubmitting ? '0 2px 8px rgba(38, 195, 192, 0.3)' : 'none',
              opacity: isSubmitting ? 0.7 : 1
            }}
            onMouseEnter={(e) => {
              if (isFormComplete && !isSubmitting) {
                e.target.style.backgroundColor = '#22B0AD';
              } else {
                e.target.style.backgroundColor = mode === 'dark' ? '#555' : '#777';
              }
            }}
            onMouseLeave={(e) => {
              if (isFormComplete && !isSubmitting) {
                e.target.style.backgroundColor = '#26C3C0';
              } else {
                e.target.style.backgroundColor = mode === 'dark' ? '#444' : '#666';
              }
            }}
          >
            {isSubmitting ? (isEditMode ? 'Updating...' : 'Uploading...') : (isEditMode ? 'Update Document' : 'Upload Document')}
          </button>
        </form>
      </div>
      </>
    </ThemeProvider>
  );
};

export default DocumentForm; 